import React, { useState } from 'react';
import { Package, Plane, CheckCircle2, XCircle, ArrowRight, DollarSign, Users, Globe, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const RelocationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    weight: ''
  });
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Quote Request Received!",
      description: "Our relocation team will contact you within 24 hours with your personalized quote."
    });
    setFormData({ name: '', email: '', phone: '', destination: '', weight: '' });
  };

  const allowedItems = [
    'Clothes',
    'Books',
    'Kitchen items',
    'Personal belongings',
    'Shoes and accessories',
    'Small household items',
    'Bedding and linens'
  ];

  const prohibitedItems = [
    'Liquids',
    'Flammable items',
    'Batteries',
    'Cash or jewelry',
    'Restricted electronics',
    'Hazardous materials'
  ];

  const idealFor = [
    { icon: Users, text: 'Students returning home' },
    { icon: Globe, text: 'Families relocating internationally' },
    { icon: Plane, text: 'Work visa holders leaving Canada' },
    { icon: Package, text: 'Permanent relocation shipments' }
  ];

  const steps = [
    {
      number: 1,
      title: 'Request Quote',
      description: 'Contact Shippzy and provide shipment details including weight, destination, and preferred pickup location.'
    },
    {
      number: 2,
      title: 'Schedule Pickup',
      description: 'Our team schedules pickup from your home or you may drop off at a Shippzy location.'
    },
    {
      number: 3,
      title: 'Packing & Documentation',
      description: 'Ensure items are packed securely. Shippzy assists with required shipping documentation.'
    },
    {
      number: 4,
      title: 'Air Cargo Shipping',
      description: 'Shipment is transported through international airline cargo partners.'
    },
    {
      number: 5,
      title: 'Delivery at Destination',
      description: 'Shipment arrives at the destination airport or may be arranged for local delivery.'
    }
  ];

  const whyChoose = [
    'Affordable bulk shipping rates',
    'Trusted global courier and airline partners',
    'Door pickup available in many areas',
    'Worldwide shipping coverage',
    'Customs documentation support',
    'Transparent pricing'
  ];

  const optionalServices = [
    'Door pickup service',
    'Packing assistance',
    'Insurance coverage (optional)',
    'Door delivery at destination',
    'Extra documentation assistance'
  ];

  return (
    <>
      <Helmet>
        <title>International Relocation Services Canada | Household Shipping Worldwide | Shippzy</title>
        <meta name="description" content="Professional international relocation and household shipping from Canada. Move your belongings worldwide with Shippzy's door to door cargo and relocation services. Free pickup available." />
        <link rel="canonical" href="https://www.shippzy.ca/relocation" />
      </Helmet>
      
      <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">International Relocation Shipping from Canada</h1>
            <p className="text-2xl text-gray-300 mb-8">Affordable international relocation shipping with trusted global carriers.</p>
          </div>

          <div className="max-w-4xl mx-auto bg-gray-800 rounded-2xl p-8 border border-gray-700">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">Moving back home or relocating internationally?</h2>
              <p className="text-xl text-gray-300">Ship your belongings safely with Shippzy.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-gradient-to-r from-red-600 to-yellow-500 border-0 text-white">
                <CardContent className="pt-6 text-center">
                  <DollarSign className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-xl font-bold mb-2">Airport-to-Airport</h3>
                  <p className="text-3xl font-bold">$7.99 CAD/kg</p>
                  <p className="text-sm mt-2">Min 100kg | India only</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-700 border-gray-600 text-white">
                <CardContent className="pt-6 text-center">
                  <Plane className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-xl font-bold mb-2">Bulk Doorstep</h3>
                  <p className="text-3xl font-bold">$8.49 CAD/kg</p>
                  <p className="text-sm mt-2">Min 138kg | 4 countries</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-700 border-gray-600 text-white">
                <CardContent className="pt-6 text-center">
                  <Package className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-xl font-bold mb-2">Standard Doorstep</h3>
                  <p className="text-3xl font-bold">$9.99 CAD/kg</p>
                  <p className="text-sm mt-2">Min 23kg | 4 countries</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What is Relocation Shipping */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">What is Relocation Shipping?</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center max-w-4xl mx-auto">
            Shippzy relocation shipping is designed for individuals moving internationally who want to send their personal belongings, household items, clothing, books, and other personal goods at affordable bulk shipping rates.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {idealFor.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-gray-800 font-medium">{item.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Relocation Shipping Pricing</h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Airport to Airport */}
            <Card className="border-2 border-red-500 shadow-xl">
              <CardContent className="pt-8 text-center">
                <p className="text-lg text-gray-700 mb-2">Airport-to-Airport</p>
                <p className="text-5xl font-bold text-red-600 mb-2">$7.99</p>
                <p className="text-gray-600 mb-4">CAD per kg</p>
                <div className="bg-gray-100 p-3 rounded-lg mb-4">
                  <p className="text-sm font-semibold text-gray-900">Min: 100 kg</p>
                  <p className="text-sm text-gray-700">India only</p>
                </div>
                <p className="text-sm text-gray-600">Airport collection</p>
              </CardContent>
            </Card>

            {/* Bulk Doorstep */}
            <Card className="border-2 border-yellow-500 shadow-xl">
              <CardContent className="pt-8 text-center">
                <p className="text-lg text-gray-700 mb-2">Bulk Doorstep</p>
                <p className="text-5xl font-bold text-yellow-600 mb-2">$8.49</p>
                <p className="text-gray-600 mb-4">CAD per kg</p>
                <div className="bg-gray-100 p-3 rounded-lg mb-4">
                  <p className="text-sm font-semibold text-gray-900">Min: 138 kg</p>
                  <p className="text-sm text-gray-700">IN, UK, UAE, NP</p>
                </div>
                <p className="text-sm text-gray-600">Doorstep delivery</p>
              </CardContent>
            </Card>

            {/* Standard Doorstep */}
            <Card className="border-2 border-green-500 shadow-xl">
              <CardContent className="pt-8 text-center">
                <p className="text-lg text-gray-700 mb-2">Standard Doorstep</p>
                <p className="text-5xl font-bold text-green-600 mb-2">$9.99</p>
                <p className="text-gray-600 mb-4">CAD per kg</p>
                <div className="bg-gray-100 p-3 rounded-lg mb-4">
                  <p className="text-sm font-semibold text-gray-900">Min: 23 kg</p>
                  <p className="text-sm text-gray-700">IN, UK, UAE, NP</p>
                </div>
                <p className="text-sm text-gray-600">Doorstep delivery</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Eligibility Requirements */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-red-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Basic Eligibility Requirements</h2>
          <Card className="shadow-lg">
            <CardContent className="pt-6">
              <p className="text-gray-700 mb-4">To qualify for relocation shipping rates, the following conditions typically apply:</p>
              <ul className="space-y-3">
                {[
                  'Minimum shipment weight 100 kg',
                  'Passenger must have a valid one-way international air ticket',
                  'Shipment must contain personal belongings only',
                  'Proper documentation must be provided before shipping'
                ].map((req, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-800">{req}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Allowed and Prohibited Items */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Allowed Items */}
            <Card className="border-2 border-green-300">
              <CardHeader className="bg-green-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  What Items Can Be Sent?
                </CardTitle>
                <CardDescription>Relocation shipments may include:</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-2">
                  {allowedItems.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600 mt-4 italic">Items must be non-commercial and used personal goods.</p>
              </CardContent>
            </Card>

            {/* Prohibited Items */}
            <Card className="border-2 border-red-300">
              <CardHeader className="bg-red-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <XCircle className="w-6 h-6 text-red-600" />
                  Items Not Allowed
                </CardTitle>
                <CardDescription>The following items cannot be shipped:</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-2">
                  {prohibitedItems.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <XCircle className="w-5 h-5 text-red-500" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600 mt-4 italic">Final acceptance depends on airline and customs regulations.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-center">How the Relocation Process Works</h2>
          <div className="space-y-6">
            {steps.map((step, index) => (
              <Card key={index} className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex gap-6 items-start">
                    <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-bold text-white">{step.number}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      <p className="text-gray-300">{step.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Customs Clearance */}
      <section className="py-16 bg-yellow-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">Customs Clearance</h2>
          <Card className="border-2 border-yellow-400">
            <CardContent className="pt-6">
              <p className="text-gray-700 mb-4">
                Shippzy assists with customs documentation and clearance support.
              </p>
              <p className="text-gray-700 mb-4">
                However, any customs duties or taxes imposed by the destination country must be paid by the sender or recipient as required by local authorities.
              </p>
              <p className="text-gray-900 font-semibold">
                Shippzy does not guarantee duty-free clearance.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Choose Shippzy */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Why Choose Shippzy for Relocation</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((reason, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="pt-6 flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-800">{reason}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Optional Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Optional Services</h2>
          <Card className="shadow-lg">
            <CardContent className="pt-6">
              <p className="text-gray-700 mb-4">Additional services may include:</p>
              <ul className="space-y-3">
                {optionalServices.map((service, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <ArrowRight className="w-5 h-5 text-red-600" />
                    <span className="text-gray-800">{service}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-yellow-500">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white mb-8">
            <h2 className="text-4xl font-bold mb-4">Relocating Internationally?</h2>
            <p className="text-xl">Ship your belongings with Shippzy today.</p>
          </div>

          <Card className="shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Get a Relocation Shipping Quote</CardTitle>
              <CardDescription className="text-center">Fill out the form and we'll contact you within 24 hours</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+1 (123) 456-7890"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Destination Country *</label>
                  <Input
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    required
                    placeholder="India, UK, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Weight (kg) *</label>
                  <Input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    required
                    placeholder="100"
                    min="100"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-12 text-lg"
                >
                  Get Quote Now
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-white">
            <p className="text-lg mb-2">Or contact us directly:</p>
            <p className="text-xl font-semibold">Phone: +1 (437) 616-0758</p>
            <p className="text-xl font-semibold">Email: ship@shippzy.ca</p>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default RelocationPage;
